import { useState, useEffect, useCallback } from "react";
import useToken from "../../../../hooks/auth/useToken";
import {
  systemAnnouncementService,
  SystemAnnouncement,
  SystemAnnouncementCreateData,
  SystemAnnouncementUpdateData,
} from "../../services/systemAnnouncementService";
import { StatusFilter } from "../../utils/types";

export const useAnnouncements = () => {
  const { token } = useToken();
  const [announcements, setAnnouncements] = useState<SystemAnnouncement[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("");

  const loadAnnouncements = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    try {
      const response = await systemAnnouncementService.getAll(token, 0, 100, statusFilter || undefined);
      setAnnouncements(response.announcements);
    } catch (error) {
      console.error("Error loading announcements:", error);
    } finally {
      setLoading(false);
    }
  }, [token, statusFilter]);

  const createAnnouncement = async (data: SystemAnnouncementCreateData, imageFile?: File | null) => {
    if (!token) return;

    try {
      const createData: SystemAnnouncementCreateData = {
        title: data.title,
        status: data.status,
        is_block: data.is_block,
      };

      if (data.description?.trim()) {
        createData.description = data.description;
      }
      if (imageFile) {
        createData.image = imageFile;
      }

      await systemAnnouncementService.create(token, createData);
      await loadAnnouncements();
    } catch (error) {
      console.error("Error creating announcement:", error);
      throw error;
    }
  };

  const updateAnnouncement = async (id: string, data: SystemAnnouncementUpdateData, imageFile?: File | null) => {
    if (!token) return;

    try {
      const updateData: SystemAnnouncementUpdateData = {
        title: data.title,
        status: data.status,
        is_block: data.is_block,
      };

      if (data.description?.trim()) {
        updateData.description = data.description;
      }
      if (imageFile) {
        updateData.image = imageFile;
      }

      await systemAnnouncementService.update(token, id, updateData);
      await loadAnnouncements();
    } catch (error) {
      console.error("Error updating announcement:", error);
      throw error;
    }
  };

  const deleteAnnouncement = async (id: string) => {
    if (!token) return;

    try {
      await systemAnnouncementService.delete(token, id);
      await loadAnnouncements();
    } catch (error) {
      console.error("Error deleting announcement:", error);
      throw error;
    }
  };

  useEffect(() => {
    loadAnnouncements();
  }, [loadAnnouncements]);

  return {
    announcements,
    loading,
    statusFilter,
    setStatusFilter,
    loadAnnouncements,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
  };
};
